import type { CollaborationConversationSummary } from "../services/collaboration";

export interface AgentStatusStreamEvent {
  type: "agent_status";
  conversationId: number;
  agentStatus: string;
  agentLastHeartbeatAt: string;
  agentCurrentTask: string | null;
}

export interface AgentStatusStreamSnapshot {
  type: "agent_status_snapshot";
  pushActive: boolean;
  agents: AgentStatusStreamEvent[];
}

export type AgentStatusStreamFrame =
  | AgentStatusStreamEvent
  | AgentStatusStreamSnapshot;

const asObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const ISO_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(?:Z|[+-](\d{2}):(\d{2}))$/;

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const parseTimestamp = (value: string | null): number | null => {
  if (!value) return null;
  const match = ISO_TIMESTAMP_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === undefined ? 0 : Number(match[8]);
  const offsetMinute = match[9] === undefined ? 0 : Number(match[9]);
  const daysInMonth = [
    0,
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth[month] ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 23 ||
    offsetMinute > 59
  ) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Parse the backend-owned status event. Invalid or unordered events are
 * ignored rather than manufacturing a plausible local state.
 */
export const normalizeAgentStatusStreamEvent = (
  value: unknown,
): AgentStatusStreamEvent | null => {
  const event = asObject(value);
  if (!event || event.type !== "agent_status") return null;

  const conversationId = event.conversation_id;
  if (
    typeof conversationId !== "number" ||
    !Number.isSafeInteger(conversationId) ||
    conversationId <= 0
  ) {
    return null;
  }
  if (typeof event.agent_status !== "string" || !event.agent_status.trim()) {
    return null;
  }
  if (
    typeof event.agent_last_heartbeat_at !== "string" ||
    parseTimestamp(event.agent_last_heartbeat_at) === null
  ) {
    return null;
  }
  if (
    event.agent_current_task !== null &&
    typeof event.agent_current_task !== "string"
  ) {
    return null;
  }

  return {
    type: "agent_status",
    conversationId,
    agentStatus: event.agent_status,
    agentLastHeartbeatAt: event.agent_last_heartbeat_at,
    agentCurrentTask: event.agent_current_task,
  };
};

/** Parse one status-stream frame without manufacturing readiness or state. */
export const normalizeAgentStatusStreamFrame = (
  value: unknown,
): AgentStatusStreamFrame | null => {
  const frame = asObject(value);
  if (!frame) return null;

  if (frame.type === "agent_status") {
    return normalizeAgentStatusStreamEvent(frame);
  }
  if (
    frame.type !== "agent_status_snapshot" ||
    typeof frame.push_active !== "boolean" ||
    !Array.isArray(frame.agents)
  ) {
    return null;
  }

  return {
    type: "agent_status_snapshot",
    pushActive: frame.push_active,
    // A corrupt member is not a reported state. Keep the listener readiness
    // observable and apply only the valid, timestamped members; the retained
    // summary reconciliation supplies anything omitted here.
    agents: frame.agents
      .map(normalizeAgentStatusStreamEvent)
      .filter((event): event is AgentStatusStreamEvent => event !== null),
  };
};

const isAtLeastAsFresh = (
  candidate: string | null,
  current: string | null,
): boolean => {
  const candidateTimestamp = parseTimestamp(candidate);
  const currentTimestamp = parseTimestamp(current);
  if (candidateTimestamp === null) return currentTimestamp === null;
  if (currentTimestamp === null) return true;
  return candidateTimestamp >= currentTimestamp;
};

/** Apply one reported status transition without disturbing control tiles. */
export const applyAgentStatusStreamEvent = (
  summaries: CollaborationConversationSummary[],
  event: AgentStatusStreamEvent,
): CollaborationConversationSummary[] =>
  summaries.map((summary) => {
    if (summary.id !== event.conversationId) return summary;
    if (
      !isAtLeastAsFresh(
        event.agentLastHeartbeatAt,
        summary.agentLastHeartbeatAt,
      )
    ) {
      return summary;
    }
    return {
      ...summary,
      agentStatus: event.agentStatus,
      agentLastHeartbeatAt: event.agentLastHeartbeatAt,
      agentCurrentTask: event.agentCurrentTask,
    };
  });

/**
 * Merge a reconciliation snapshot without allowing a response started before
 * an SSE transition to roll the tile back when it lands afterward.
 */
export const mergeAgentStatusSnapshot = (
  current: CollaborationConversationSummary[],
  incoming: CollaborationConversationSummary[],
): CollaborationConversationSummary[] => {
  const currentById = new Map(current.map((summary) => [summary.id, summary]));
  return incoming.map((summary) => {
    const previous = currentById.get(summary.id);
    if (
      !previous ||
      isAtLeastAsFresh(
        summary.agentLastHeartbeatAt,
        previous.agentLastHeartbeatAt,
      )
    ) {
      return summary;
    }
    return {
      ...summary,
      agentStatus: previous.agentStatus,
      agentLastHeartbeatAt: previous.agentLastHeartbeatAt,
      agentCurrentTask: previous.agentCurrentTask,
    };
  });
};

/** Merge the full summary returned by an agent-persona update. */
export const mergeAgentPersonaPatchResponse = (
  current: CollaborationConversationSummary,
  updated: CollaborationConversationSummary,
): CollaborationConversationSummary => ({
  ...current,
  // Persona PATCH owns only these fields. Status is written by SSE events or
  // heartbeat-ranked summary reconciliation, so a delayed persona response
  // cannot become a third, unranked status writer.
  agentName: updated.agentName,
  systemPromptOverride: updated.systemPromptOverride,
});
